/* istanbul ignore file */
import {
    ConsentStatus,
    ControllerStatus,
    Country,
    IDTokenProvider,
    LicensableCharacteristic,
    LicenseTermEnum,
    LicensedStatus,
    ManagementActionCategory,
    ManagementActionType,
    PartnerType,
    PermissionsSourceTypeChoices,
    ProductType,
    Region,
    TenantPermission,
    TenantType
} from '@br/idm-api';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

export function useConsentStatusNames(): Record<ConsentStatus, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [ConsentStatus.Pending]: t('enums:ConsentStatus.Pending', 'Pending'),
        [ConsentStatus.Granted]: t('enums:ConsentStatus.Granted', 'Granted'),
        [ConsentStatus.Rejected]: t('enums:ConsentStatus.Rejected', 'Rejected'),
        [ConsentStatus.Revoked]: t('enums:ConsentStatus.Revoked', 'Revoked'),
    }), [t]);
}

export function useControllerStatusNames(): Record<ControllerStatus, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [ControllerStatus.Pending]: t('enums:ControllerStatus.Pending', 'Pending creation'),
        [ControllerStatus.Ready]: t('enums:ControllerStatus.Ready', 'Ready'),
    }), [t]);
}

export const CountryNames = {
    /* eslint-disable i18next/no-literal-string */
    [Country.Af]: 'Afghanistan',
    [Country.Ax]: 'Åland Islands',
    [Country.Al]: 'Albania',
    [Country.Dz]: 'Algeria',
    [Country.As]: 'American Samoa',
    [Country.Ad]: 'Andorra',
    [Country.Ao]: 'Angola',
    [Country.Ai]: 'Anguilla',
    [Country.Aq]: 'Antarctica',
    [Country.Ag]: 'Antigua & Barbuda',
    [Country.Ar]: 'Argentina',
    [Country.Am]: 'Armenia',
    [Country.Aw]: 'Aruba',
    [Country.Au]: 'Australia',
    [Country.At]: 'Austria',
    [Country.Az]: 'Azerbaijan',
    [Country.Bs]: 'Bahamas',
    [Country.Bh]: 'Bahrain',
    [Country.Bd]: 'Bangladesh',
    [Country.Bb]: 'Barbados',
    [Country.By]: 'Belarus',
    [Country.Be]: 'Belgium',
    [Country.Bz]: 'Belize',
    [Country.Bj]: 'Benin',
    [Country.Bm]: 'Bermuda',
    [Country.Bt]: 'Bhutan',
    [Country.Bo]: 'Bolivia',
    [Country.Ba]: 'Bosnia & Herzegovina',
    [Country.Bw]: 'Botswana',
    [Country.Bv]: 'Bouvet Island',
    [Country.Br]: 'Brazil',
    [Country.Io]: 'British Indian Ocean Territory',
    [Country.Vg]: 'British Virgin Islands',
    [Country.Bn]: 'Brunei',
    [Country.Bg]: 'Bulgaria',
    [Country.Bf]: 'Burkina Faso',
    [Country.Bi]: 'Burundi',
    [Country.Kh]: 'Cambodia',
    [Country.Cm]: 'Cameroon',
    [Country.Ca]: 'Canada',
    [Country.Cv]: 'Cape Verde',
    [Country.Bq]: 'Caribbean Netherlands',
    [Country.Ky]: 'Cayman Islands',
    [Country.Cf]: 'Central African Republic',
    [Country.Td]: 'Chad',
    [Country.Cl]: 'Chile',
    [Country.Cn]: 'China',
    [Country.Cx]: 'Christmas Island',
    [Country.Cc]: 'Cocos (Keeling) Islands',
    [Country.Co]: 'Colombia',
    [Country.Km]: 'Comoros',
    [Country.Cg]: 'Congo - Brazzaville',
    [Country.Cd]: 'Congo - Kinshasa',
    [Country.Ck]: 'Cook Islands',
    [Country.Cr]: 'Costa Rica',
    [Country.Ci]: 'Côte d’Ivoire',
    [Country.Hr]: 'Croatia',
    [Country.Cu]: 'Cuba',
    [Country.Cw]: 'Curaçao',
    [Country.Cy]: 'Cyprus',
    [Country.Cz]: 'Czechia',
    [Country.Dk]: 'Denmark',
    [Country.Dj]: 'Djibouti',
    [Country.Dm]: 'Dominica',
    [Country.Do]: 'Dominican Republic',
    [Country.Ec]: 'Ecuador',
    [Country.Eg]: 'Egypt',
    [Country.Sv]: 'El Salvador',
    [Country.Gq]: 'Equatorial Guinea',
    [Country.Er]: 'Eritrea',
    [Country.Ee]: 'Estonia',
    [Country.Sz]: 'Eswatini',
    [Country.Et]: 'Ethiopia',
    [Country.Fk]: 'Falkland Islands',
    [Country.Fo]: 'Faroe Islands',
    [Country.Fj]: 'Fiji',
    [Country.Fi]: 'Finland',
    [Country.Fr]: 'France',
    [Country.Gf]: 'French Guiana',
    [Country.Pf]: 'French Polynesia',
    [Country.Tf]: 'French Southern Territories',
    [Country.Ga]: 'Gabon',
    [Country.Gm]: 'Gambia',
    [Country.Ge]: 'Georgia',
    [Country.De]: 'Germany',
    [Country.Gh]: 'Ghana',
    [Country.Gi]: 'Gibraltar',
    [Country.Gr]: 'Greece',
    [Country.Gl]: 'Greenland',
    [Country.Gd]: 'Grenada',
    [Country.Gp]: 'Guadeloupe',
    [Country.Gu]: 'Guam',
    [Country.Gt]: 'Guatemala',
    [Country.Gg]: 'Guernsey',
    [Country.Gn]: 'Guinea',
    [Country.Gw]: 'Guinea-Bissau',
    [Country.Gy]: 'Guyana',
    [Country.Ht]: 'Haiti',
    [Country.Hm]: 'Heard & McDonald Islands',
    [Country.Hn]: 'Honduras',
    [Country.Hk]: 'Hong Kong SAR China',
    [Country.Hu]: 'Hungary',
    [Country.Is]: 'Iceland',
    [Country.In]: 'India',
    [Country.Id]: 'Indonesia',
    [Country.Ir]: 'Iran',
    [Country.Iq]: 'Iraq',
    [Country.Ie]: 'Ireland',
    [Country.Im]: 'Isle of Man',
    [Country.Il]: 'Israel',
    [Country.It]: 'Italy',
    [Country.Jm]: 'Jamaica',
    [Country.Jp]: 'Japan',
    [Country.Je]: 'Jersey',
    [Country.Jo]: 'Jordan',
    [Country.Kz]: 'Kazakhstan',
    [Country.Ke]: 'Kenya',
    [Country.Ki]: 'Kiribati',
    [Country.Kw]: 'Kuwait',
    [Country.Kg]: 'Kyrgyzstan',
    [Country.La]: 'Laos',
    [Country.Lv]: 'Latvia',
    [Country.Lb]: 'Lebanon',
    [Country.Ls]: 'Lesotho',
    [Country.Lr]: 'Liberia',
    [Country.Ly]: 'Libya',
    [Country.Li]: 'Liechtenstein',
    [Country.Lt]: 'Lithuania',
    [Country.Lu]: 'Luxembourg',
    [Country.Mo]: 'Macao SAR China',
    [Country.Mg]: 'Madagascar',
    [Country.Mw]: 'Malawi',
    [Country.My]: 'Malaysia',
    [Country.Mv]: 'Maldives',
    [Country.Ml]: 'Mali',
    [Country.Mt]: 'Malta',
    [Country.Mh]: 'Marshall Islands',
    [Country.Mq]: 'Martinique',
    [Country.Mr]: 'Mauritania',
    [Country.Mu]: 'Mauritius',
    [Country.Yt]: 'Mayotte',
    [Country.Mx]: 'Mexico',
    [Country.Fm]: 'Micronesia',
    [Country.Md]: 'Moldova',
    [Country.Mc]: 'Monaco',
    [Country.Mn]: 'Mongolia',
    [Country.Me]: 'Montenegro',
    [Country.Ms]: 'Montserrat',
    [Country.Ma]: 'Morocco',
    [Country.Mz]: 'Mozambique',
    [Country.Mm]: 'Myanmar (Burma)',
    [Country.Na]: 'Namibia',
    [Country.Nr]: 'Nauru',
    [Country.Np]: 'Nepal',
    [Country.Nl]: 'Netherlands',
    [Country.Nc]: 'New Caledonia',
    [Country.Nz]: 'New Zealand',
    [Country.Ni]: 'Nicaragua',
    [Country.Ne]: 'Niger',
    [Country.Ng]: 'Nigeria',
    [Country.Nu]: 'Niue',
    [Country.Nf]: 'Norfolk Island',
    [Country.Kp]: 'North Korea',
    [Country.Mk]: 'North Macedonia',
    [Country.Mp]: 'Northern Mariana Islands',
    [Country.No]: 'Norway',
    [Country.Om]: 'Oman',
    [Country.Pk]: 'Pakistan',
    [Country.Pw]: 'Palau',
    [Country.Ps]: 'Palestinian Territories',
    [Country.Pa]: 'Panama',
    [Country.Pg]: 'Papua New Guinea',
    [Country.Py]: 'Paraguay',
    [Country.Pe]: 'Peru',
    [Country.Ph]: 'Philippines',
    [Country.Pn]: 'Pitcairn Islands',
    [Country.Pl]: 'Poland',
    [Country.Pt]: 'Portugal',
    [Country.Pr]: 'Puerto Rico',
    [Country.Qa]: 'Qatar',
    [Country.Re]: 'Réunion',
    [Country.Ro]: 'Romania',
    [Country.Ru]: 'Russia',
    [Country.Rw]: 'Rwanda',
    [Country.Ws]: 'Samoa',
    [Country.Sm]: 'San Marino',
    [Country.St]: 'São Tomé & Príncipe',
    [Country.Sa]: 'Saudi Arabia',
    [Country.Sn]: 'Senegal',
    [Country.Rs]: 'Serbia',
    [Country.Sc]: 'Seychelles',
    [Country.Sl]: 'Sierra Leone',
    [Country.Sg]: 'Singapore',
    [Country.Sx]: 'Sint Maarten',
    [Country.Sk]: 'Slovakia',
    [Country.Si]: 'Slovenia',
    [Country.Sb]: 'Solomon Islands',
    [Country.So]: 'Somalia',
    [Country.Za]: 'South Africa',
    [Country.Gs]: 'South Georgia & South Sandwich Islands',
    [Country.Kr]: 'South Korea',
    [Country.Ss]: 'South Sudan',
    [Country.Es]: 'Spain',
    [Country.Lk]: 'Sri Lanka',
    [Country.Bl]: 'St. Barthélemy',
    [Country.Sh]: 'St. Helena',
    [Country.Kn]: 'St. Kitts & Nevis',
    [Country.Lc]: 'St. Lucia',
    [Country.Mf]: 'St. Martin',
    [Country.Pm]: 'St. Pierre & Miquelon',
    [Country.Vc]: 'St. Vincent & Grenadines',
    [Country.Sd]: 'Sudan',
    [Country.Sr]: 'Suriname',
    [Country.Sj]: 'Svalbard & Jan Mayen',
    [Country.Se]: 'Sweden',
    [Country.Ch]: 'Switzerland',
    [Country.Sy]: 'Syria',
    [Country.Tw]: 'Taiwan',
    [Country.Tj]: 'Tajikistan',
    [Country.Tz]: 'Tanzania',
    [Country.Th]: 'Thailand',
    [Country.Tl]: 'Timor-Leste',
    [Country.Tg]: 'Togo',
    [Country.Tk]: 'Tokelau',
    [Country.To]: 'Tonga',
    [Country.Tt]: 'Trinidad & Tobago',
    [Country.Tn]: 'Tunisia',
    [Country.Tr]: 'Türkiye',
    [Country.Tm]: 'Turkmenistan',
    [Country.Tc]: 'Turks & Caicos Islands',
    [Country.Tv]: 'Tuvalu',
    [Country.Um]: 'U.S. Outlying Islands',
    [Country.Vi]: 'U.S. Virgin Islands',
    [Country.Ug]: 'Uganda',
    [Country.Ua]: 'Ukraine',
    [Country.Ae]: 'United Arab Emirates',
    [Country.Gb]: 'United Kingdom',
    [Country.Us]: 'United States',
    [Country.Uy]: 'Uruguay',
    [Country.Uz]: 'Uzbekistan',
    [Country.Vu]: 'Vanuatu',
    [Country.Va]: 'Vatican City',
    [Country.Ve]: 'Venezuela',
    [Country.Vn]: 'Vietnam',
    [Country.Wf]: 'Wallis & Futuna',
    [Country.Eh]: 'Western Sahara',
    [Country.Ye]: 'Yemen',
    [Country.Zm]: 'Zambia',
    [Country.Zw]: 'Zimbabwe',
    /* eslint-enable i18next/no-literal-string */
};

export function useIDTokenProviderNames(): Record<IDTokenProvider, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [IDTokenProvider.Hpid]: t('enums:IDTokenProvider.Hpid', 'HP ID'),
        [IDTokenProvider.TenantAzure]: t('enums:IDTokenProvider.TenantAzure', 'Microsoft Entra ID'),
        [IDTokenProvider.StaffAzure]: t('enums:IDTokenProvider.StaffAzure', 'HP Admin'),
    }), [t]);
}

export function useLicensableCharacteristicNames(): Record<LicensableCharacteristic, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [LicensableCharacteristic.Wpt]: t('enums:LicensableCharacteristic.Wpt', 'Protect & Trace'),
        [LicensableCharacteristic.Wcmnb]: t('enums:LicensableCharacteristic.Wcmnb', 'Wolf Connect (MNB)'),
        [LicensableCharacteristic.Wcwwan]: t('enums:LicensableCharacteristic.Wcwwan', 'Wolf Connect (WWAN)'),
        [LicensableCharacteristic.Iso]: t('enums:LicensableCharacteristic.Iso', 'Isolation'),
        [LicensableCharacteristic.Mon]: t('enums:LicensableCharacteristic.Mon', 'Monitoring'),
        [LicensableCharacteristic.CredProt]: t('enums:LicensableCharacteristic.CredProt', 'Credential Protection'),
        [LicensableCharacteristic.ProtApps]: t('enums:LicensableCharacteristic.ProtApps', 'Protected Apps'),
        [LicensableCharacteristic.ThinCli]: t('enums:LicensableCharacteristic.ThinCli', 'Thin Client'),
        [LicensableCharacteristic.SignedSet]: t('enums:LicensableCharacteristic.SignedSet', 'Signed Settings'),
    }), [t]);
}

export function useLicenseTermEnumNames(): Record<LicenseTermEnum, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [LicenseTermEnum.YEARS_1]: t('enums:LicenseTermEnum.YEARS_1', '1 year'),
        [LicenseTermEnum.YEARS_2]: t('enums:LicenseTermEnum.YEARS_2', '2 years'),
        [LicenseTermEnum.YEARS_3]: t('enums:LicenseTermEnum.YEARS_3', '3 years'),
        [LicenseTermEnum.YEARS_4]: t('enums:LicenseTermEnum.YEARS_4', '4 years'),
        [LicenseTermEnum.YEARS_5]: t('enums:LicenseTermEnum.YEARS_5', '5 years'),
    }), [t]);
}

export function useLicensedStatusNames(): Record<LicensedStatus, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [LicensedStatus.Unlicensed]: t('enums:LicensedStatus.Unlicensed', 'Unlicensed'),
        [LicensedStatus.Licensed]: t('enums:LicensedStatus.Licensed', 'Licensed'),
        [LicensedStatus.LicensedEditionpc]: t('enums:LicensedStatus.LicensedEditionpc', 'Licensed (Edition PC)'),
        [LicensedStatus.LicensedDirect]: t('enums:LicensedStatus.LicensedDirect', 'Licensed (built in license)'),
    }), [t]);
}

export function useManagementActionCategoryNames(): Record<ManagementActionCategory, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [ManagementActionCategory.ENV]: t('enums:ManagementActionCategory.ENV', 'Environmental'),
        [ManagementActionCategory.PRODUCT]: t('enums:ManagementActionCategory.PRODUCT', 'Product'),
        [ManagementActionCategory.CONF]: t('enums:ManagementActionCategory.CONF', 'Configuration'),
        [ManagementActionCategory.INFORMATION]: t('enums:ManagementActionCategory.INFORMATION', 'Information'),
        [ManagementActionCategory.OTHER]: t('enums:ManagementActionCategory.OTHER', 'Other'),
    }), [t]);
}

export function useManagementActionTypeNames(): Record<ManagementActionType, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [ManagementActionType.UNKNOWN]: t('enums:ManagementActionType.UNKNOWN', 'Other issues'),
        [ManagementActionType.VTX_NOT_CAPABLE]: t('enums:ManagementActionType.VTX_NOT_CAPABLE', 'Hardware does not support Intel® VT-x virtualization extensions'),
        [ManagementActionType.VTX_NOT_ENABLED]: t('enums:ManagementActionType.VTX_NOT_ENABLED', 'Intel® VT-x virtualization extensions are disabled in system BIOS'),
        [ManagementActionType.EPT_DISABLED]: t('enums:ManagementActionType.EPT_DISABLED', 'Hardware does not support Intel® EPT virtualization extensions'),
        [ManagementActionType.PAE_NOT_SUPPORTED]: t('enums:ManagementActionType.PAE_NOT_SUPPORTED', 'Hardware does not support PAE'),
        [ManagementActionType.PAE_DISABLED]: t('enums:ManagementActionType.PAE_DISABLED', 'PAE disabled in the BIOS'),
        [ManagementActionType.HYPER_V_ENABLED]: t('enums:ManagementActionType.HYPER_V_ENABLED', 'HP Sure Click cannot run while Hyper-V is enabled'),
        [ManagementActionType.HYPER_V_ENABLED_VIA_GPO]: t('enums:ManagementActionType.HYPER_V_ENABLED_VIA_GPO', 'Hyper-V (Virtualization Based Security/Device Guard/Credential Guard) is enabled via Group Policy Object'),
        [ManagementActionType.KB3033929]: t('enums:ManagementActionType.KB3033929', 'Critical Windows update missing: KB3033929'),
        [ManagementActionType.AX_REQUIRED_BUT_NOT_SUPPORTED]: t('enums:ManagementActionType.AX_REQUIRED_BUT_NOT_SUPPORTED', 'Unable to support Hyper-V on this device'),
        [ManagementActionType.AX_REQUIRED_BUT_MISSING_EFI]: t('enums:ManagementActionType.AX_REQUIRED_BUT_MISSING_EFI', 'Require UEFI boot in order to support Hyper-V'),
        [ManagementActionType.AX_REQUIRED_BUT_MISSING_WIN_10]: t('enums:ManagementActionType.AX_REQUIRED_BUT_MISSING_WIN_10', 'Require Windows 10 or later in order to support Hyper-V'),
        [ManagementActionType.AX_REQUIRED_BUT_MISSING_SUPPORTED_CPU]: t('enums:ManagementActionType.AX_REQUIRED_BUT_MISSING_SUPPORTED_CPU', 'This CPU is not supported when Hyper-V is enabled'),
        [ManagementActionType.AX_REQUIRED_BUT_MISSING_SECURE_BOOT_SIGNATURE]: t('enums:ManagementActionType.AX_REQUIRED_BUT_MISSING_SECURE_BOOT_SIGNATURE', 'Require Secure Boot third party key in order to support Hyper-V'),
        [ManagementActionType.AX_REQUIRED_BUT_MISSING_VMSC_SHADOW]: t('enums:ManagementActionType.AX_REQUIRED_BUT_MISSING_VMSC_SHADOW', 'Require CPU capable of VMCS shadowing in order to support Hyper-V'),
        [ManagementActionType.HV_PROFILE_NOT_SUPPORTED]: t('enums:ManagementActionType.HV_PROFILE_NOT_SUPPORTED', 'Unable to support the requested hypervisor configuration on this device'),
        [ManagementActionType.CORRUPT_INSTALLATION]: t('enums:ManagementActionType.CORRUPT_INSTALLATION', 'Installation has been corrupted and needs to be repaired'),
        [ManagementActionType.INTERNAL_ERROR]: t('enums:ManagementActionType.INTERNAL_ERROR', 'An internal error is present'),
        [ManagementActionType.NOT_ENOUGH_DISK]: t('enums:ManagementActionType.NOT_ENOUGH_DISK', 'Insufficient disk space'),
        [ManagementActionType.UNLICENSED]: t('enums:ManagementActionType.UNLICENSED', 'HP Sure Click is not licensed'),
        [ManagementActionType.VSS_SERVICE_DISABLED]: t('enums:ManagementActionType.VSS_SERVICE_DISABLED', 'Volume Snapshot Service (VSS) is disabled'),
        [ManagementActionType.VBOX_ALREADY_INSTALLED]: t('enums:ManagementActionType.VBOX_ALREADY_INSTALLED', 'Incompatible virtualization software installed (VirtualBox)'),
        [ManagementActionType.WINDOWSUPDATE_REBOOT_PENDING]: t('enums:ManagementActionType.WINDOWSUPDATE_REBOOT_PENDING', 'Awaiting pending reboot for Windows Update'),
        [ManagementActionType.WINDOWSUPDATE_IN_PROGRESS]: t('enums:ManagementActionType.WINDOWSUPDATE_IN_PROGRESS', 'Windows Update is in progress'),
        [ManagementActionType.ACTIVE_IN_ANOTHER_SESSION]: t('enums:ManagementActionType.ACTIVE_IN_ANOTHER_SESSION', 'Another user is logged in and using HP Sure Click on the same computer'),
        [ManagementActionType.NOT_ENOUGH_FREE_MEMORY]: t('enums:ManagementActionType.NOT_ENOUGH_FREE_MEMORY', 'Insufficient free memory available'),
        [ManagementActionType.BACKGROUND_TEMPLATE_BLOCKED]: t('enums:ManagementActionType.BACKGROUND_TEMPLATE_BLOCKED', 'Background creation of updated template blocked'),
        [ManagementActionType.MICROVM_LOAD_ERROR]: t('enums:ManagementActionType.MICROVM_LOAD_ERROR', 'Micro-VM loading failed'),
        [ManagementActionType.VSENTRY_REBOOT_PENDING]: t('enums:ManagementActionType.VSENTRY_REBOOT_PENDING', 'Endpoint requires a reboot to protect the system'),
        [ManagementActionType.VBA_MISSING]: t('enums:ManagementActionType.VBA_MISSING', 'The VBA component must be installed with Microsoft Office'),
        [ManagementActionType.WINDOWS_UPDATES_MISSING]: t('enums:ManagementActionType.WINDOWS_UPDATES_MISSING', 'Required Windows Updates are not installed'),
        [ManagementActionType.LANGUAGE_PACK_MISSING]: t('enums:ManagementActionType.LANGUAGE_PACK_MISSING', 'No supported Windows language pack is installed'),
        [ManagementActionType.MUI_MISMATCH]: t('enums:ManagementActionType.MUI_MISMATCH', 'Windows 8.1 Update KB2919355 needs to be reapplied'),
        [ManagementActionType.MS_SSCP_NOT_ENABLED]: t('enums:ManagementActionType.MS_SSCP_NOT_ENABLED', 'Microsoft Software Shadow Copy Provider service is disabled'),
        [ManagementActionType.UNSUPPORTED_USER_LANGUAGE]: t('enums:ManagementActionType.UNSUPPORTED_USER_LANGUAGE', 'The user\'s Windows display language is not supported'),
        [ManagementActionType.UNSUPPORTED_AMD_CPU]: t('enums:ManagementActionType.UNSUPPORTED_AMD_CPU', 'Unsupported AMD CPU family'),
        [ManagementActionType.LICENSE_EXPIRY]: t('enums:ManagementActionType.LICENSE_EXPIRY', 'HP Sure Click license will expire soon'),
        [ManagementActionType.POLICY_NOT_APPLIED]: t('enums:ManagementActionType.POLICY_NOT_APPLIED', 'Failed to fetch policy from Controller'),
        [ManagementActionType.UNSUPPORTED_CONFIGURATION]: t('enums:ManagementActionType.UNSUPPORTED_CONFIGURATION', 'Unsupported configuration'),
        [ManagementActionType.UNSUPPORTED_SURE_CLICK_CPU]: t('enums:ManagementActionType.UNSUPPORTED_SURE_CLICK_CPU', 'Unsupported CPU for HP Sure Click'),
        [ManagementActionType.TEMPLATE_CREATION_FAILED]: t('enums:ManagementActionType.TEMPLATE_CREATION_FAILED', 'System template creation has failed'),
        [ManagementActionType.USER_IMAGE_CREATION_FAILED]: t('enums:ManagementActionType.USER_IMAGE_CREATION_FAILED', 'User-specific template creation has failed'),
        [ManagementActionType.TEMPLATE_CREATION_CANCELLED]: t('enums:ManagementActionType.TEMPLATE_CREATION_CANCELLED', 'An attempt to create a system template was canceled'),
        [ManagementActionType.USER_IMAGE_CREATION_CANCELLED]: t('enums:ManagementActionType.USER_IMAGE_CREATION_CANCELLED', 'An attempt to create a user-specific template was canceled'),
        [ManagementActionType.CRITICAL_TEMPLATE_PENDING]: t('enums:ManagementActionType.CRITICAL_TEMPLATE_PENDING', 'Waiting for a critical initialization to occur'),
        [ManagementActionType.DEFERRED_TEMPLATE_PENDING]: t('enums:ManagementActionType.DEFERRED_TEMPLATE_PENDING', 'Waiting for a deferred initialization to occur'),
        [ManagementActionType.NOT_ENOUGH_MEMORY]: t('enums:ManagementActionType.NOT_ENOUGH_MEMORY', 'Memory requirements have not been met'),
        [ManagementActionType.EMIE_PATCH_REQUIRED]: t('enums:ManagementActionType.EMIE_PATCH_REQUIRED', 'IE 11 update required to safely support Enterprise Mode (KB2956283 or MS14-035)'),
        [ManagementActionType.EPM_ENABLED]: t('enums:ManagementActionType.EPM_ENABLED', 'Enhanced Protected Mode must be turned off'),
        [ManagementActionType.CHROME_FRAME_INSTALLED]: t('enums:ManagementActionType.CHROME_FRAME_INSTALLED', 'Google Chrome Frame must be uninstalled'),
        [ManagementActionType.VSENTRY_UPGRADE_AWAITING_REBOOT]: t('enums:ManagementActionType.VSENTRY_UPGRADE_AWAITING_REBOOT', 'Endpoint requires a reboot for an upgrade to take effect'),
        [ManagementActionType.OFFICE_UI_LANG_PACK_MISSING]: t('enums:ManagementActionType.OFFICE_UI_LANG_PACK_MISSING', 'No supported Office UI language pack is installed'),
        [ManagementActionType.OFFICE_NOT_ACTIVATED]: t('enums:ManagementActionType.OFFICE_NOT_ACTIVATED', 'Unable to create a template; Office is not activated. To protect this device, disable Document Protection or activate Office'),
        [ManagementActionType.INCOMPATIBLE_WINDOWS_VERSION]: t('enums:ManagementActionType.INCOMPATIBLE_WINDOWS_VERSION', 'Isolation is not supported on the current Windows version'),
        [ManagementActionType.MISCONFIGURED_CONTAINMENT]: t('enums:ManagementActionType.MISCONFIGURED_CONTAINMENT', 'Misconfiguration: Intranet/SaaS lists ignored'),
        [ManagementActionType.ERROR_INSTALLING_AX]: t('enums:ManagementActionType.ERROR_INSTALLING_AX', 'Failed to enable support for Hyper-V'),
        [ManagementActionType.UXEN_BLOCKED_BY_AX]: t('enums:ManagementActionType.UXEN_BLOCKED_BY_AX', 'Micro-virtualization blocked while enabling support for Hyper-V'),
        [ManagementActionType.THREAT_CLOUD_INACCESSIBLE]: t('enums:ManagementActionType.THREAT_CLOUD_INACCESSIBLE', 'The last attempt to contact the HP Threat Intelligence Service was unsuccessful'),
        [ManagementActionType.SUSPEND_BIT_LOCKER]: t('enums:ManagementActionType.SUSPEND_BIT_LOCKER', 'BitLocker must be suspended before the computer is shutdown/restarted'),
        [ManagementActionType.ERROR_AX_TEST_BOOT_FAIL]: t('enums:ManagementActionType.ERROR_AX_TEST_BOOT_FAIL', 'Test boot failed when enabling support for Hyper-V'),
        [ManagementActionType.ERROR_AX_FAST_BOOT_BUG]: t('enums:ManagementActionType.ERROR_AX_FAST_BOOT_BUG', 'Unable to configure the UEFI boot order when enabling support for Hyper-V'),
        [ManagementActionType.ERROR_AX_BCD_BOOT_FAIL]: t('enums:ManagementActionType.ERROR_AX_BCD_BOOT_FAIL', 'Unable to determine the boot device when enabling support for Hyper-V'),
        [ManagementActionType.SBX_STATE_HAS_ERROR]: t('enums:ManagementActionType.SBX_STATE_HAS_ERROR', 'Secure Browsing Extension encountered an error'),
        [ManagementActionType.OUTLOOK_ADDIN_DISABLED]: t('enums:ManagementActionType.OUTLOOK_ADDIN_DISABLED', 'HP Sure Click Outlook Add-in is disabled'),
        [ManagementActionType.OUTLOOK_REPLY_WITH_CHANGES_ENABLED]: t('enums:ManagementActionType.OUTLOOK_REPLY_WITH_CHANGES_ENABLED', '"Add properties to attachments to enable Reply with Changes" is enabled in Outlook'),
        [ManagementActionType.SBX_FORCE_INSTALL_FAILURE]: t('enums:ManagementActionType.SBX_FORCE_INSTALL_FAILURE', 'Corrupted local group policy folder detected'),
        [ManagementActionType.CRASHING_PRINTERS]: t('enums:ManagementActionType.CRASHING_PRINTERS', 'Some printer drivers are unusable. These printers will not be available to secure browsers or files'),
        [ManagementActionType.WINDOWS_DEFENDER_EXCLUSIONS]: t('enums:ManagementActionType.WINDOWS_DEFENDER_EXCLUSIONS', 'HP Sure Click has not been added to the Windows Defender exclusions'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_ADOBE_READER]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_ADOBE_READER', 'Application has failed to virtualize and will not be available: Adobe Reader'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_ADOBE_ACROBAT]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_ADOBE_ACROBAT', 'Application has failed to virtualize and will not be available: Adobe Acrobat'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_MS_WORD]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_MS_WORD', 'Application has failed to virtualize and will not be available: Microsoft Word'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_MS_EXCEL]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_MS_EXCEL', 'Application has failed to virtualize and will not be available: Microsoft Excel'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_MS_POWERPOINT]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_MS_POWERPOINT', 'Application has failed to virtualize and will not be available: Microsoft PowerPoint'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_MS_INTERNET_EXPLORER]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_MS_INTERNET_EXPLORER', 'Application has failed to virtualize and will not be available: Microsoft Internet Explorer'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_CHROMIUM]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_CHROMIUM', 'Application has failed to virtualize and will not be available: Chromium'),
        [ManagementActionType.FAILED_TO_VIRTUALIZE_MOZILLA_FIREFOX]: t('enums:ManagementActionType.FAILED_TO_VIRTUALIZE_MOZILLA_FIREFOX', 'Application has failed to virtualize and will not be available: Mozilla Firefox'),
        [ManagementActionType.UNLICENSED_MS_OFFICE]: t('enums:ManagementActionType.UNLICENSED_MS_OFFICE', 'Microsoft Office will not be available because it is unlicensed'),
        [ManagementActionType.SBX_UNPROTECTED_DEFAULT_BROWSER]: t('enums:ManagementActionType.SBX_UNPROTECTED_DEFAULT_BROWSER', 'Web links are being opened in an unprotected default browser'),
        [ManagementActionType.NO_SUPPORTED_OFFICE_LANGUAGES]: t('enums:ManagementActionType.NO_SUPPORTED_OFFICE_LANGUAGES', 'No supported Microsoft Office languages are installed, Microsoft Office will not be available'),
        [ManagementActionType.POLICY_TAMPERING]: t('enums:ManagementActionType.POLICY_TAMPERING', 'Stored and applied policy do not match'),
        [ManagementActionType.IDENTITY_PROTECTION_UNSUPPORTED_DEFAULT_BROWSER]: t('enums:ManagementActionType.IDENTITY_PROTECTION_UNSUPPORTED_DEFAULT_BROWSER', 'The default browser is not supported by HP Credential Protection'),
        [ManagementActionType.FAILED_TO_WRITE_OUTLOOK_ADDIN_GPO]: t('enums:ManagementActionType.FAILED_TO_WRITE_OUTLOOK_ADDIN_GPO', 'Outlook add-in has not been added to the list of add-ins that are always enabled on the endpoint'),
        [ManagementActionType.FAILED_TO_INSTALL_PRINTER_DRIVERS]: t('enums:ManagementActionType.FAILED_TO_INSTALL_PRINTER_DRIVERS', 'Printer drivers failed to install into template'),
        [ManagementActionType.RULES_FAILED_TO_LOAD]: t('enums:ManagementActionType.RULES_FAILED_TO_LOAD', 'Sure Sense is not active because base rules failed to load'),
        [ManagementActionType.MONITORING_DISABLED_DUE_EXCESS_ALERTS]: t('enums:ManagementActionType.MONITORING_DISABLED_DUE_EXCESS_ALERTS', 'Sure Sense is not active due to an excessive backlog of alerts'),
        [ManagementActionType.MONITORING_SERVICE_FAILED_TO_START]: t('enums:ManagementActionType.MONITORING_SERVICE_FAILED_TO_START', 'Sure Sense is not active because the service failed to start'),
        [ManagementActionType.BASE_RULES_FAILED_TO_LOAD]: t('enums:ManagementActionType.BASE_RULES_FAILED_TO_LOAD', 'Monitoring is not active because rules failed to load'),
        [ManagementActionType.INDEXING_HAS_NOT_COMPLETED_IN_TIME]: t('enums:ManagementActionType.INDEXING_HAS_NOT_COMPLETED_IN_TIME', 'Indexing has not completed in the given time frame'),
        [ManagementActionType.HOOKING_DISABLED_WINDOWS_VERSION]: t('enums:ManagementActionType.HOOKING_DISABLED_WINDOWS_VERSION', 'Monitoring hooking disabled on Windows version'),
        [ManagementActionType.STAGED_SEARCH_FILE_LIMIT_REACHED]: t('enums:ManagementActionType.STAGED_SEARCH_FILE_LIMIT_REACHED', 'Indexing has stopped because it has exceeded the limit for staged indexing. Check the file types being indexed or increase the staged indexing limit.'),
        [ManagementActionType.BEM_ACTION_IGNORING_OLDER_RULES]: t('enums:ManagementActionType.BEM_ACTION_IGNORING_OLDER_RULES', 'Sure Sense is ignoring base rules from a previous version'),
        [ManagementActionType.BEM_ACTION_BEMK_DISABLED_WINDOWS_VERSION]: t('enums:ManagementActionType.BEM_ACTION_BEMK_DISABLED_WINDOWS_VERSION', 'Sure Sense Malware Prevention is not active on this device as the Windows version is not supported'),
        [ManagementActionType.BEM_ACTION_SURE_SENSE_ATC_DISABLED_WINDOWS_VERSION]: t('enums:ManagementActionType.BEM_ACTION_SURE_SENSE_ATC_DISABLED_WINDOWS_VERSION', 'Sure Sense Behavioral Detection is not active on this device as the Windows version is not supported'),
        [ManagementActionType.SURE_SENSE_GENERIC_ERROR]: t('enums:ManagementActionType.SURE_SENSE_GENERIC_ERROR', 'Sure Sense has an internal error'),
        [ManagementActionType.SURE_SENSE_UNLICENSED]: t('enums:ManagementActionType.SURE_SENSE_UNLICENSED', 'Sure Sense is unlicensed'),
        [ManagementActionType.SURE_SENSE_DOWNLOAD_FAILED]: t('enums:ManagementActionType.SURE_SENSE_DOWNLOAD_FAILED', 'Sure Sense failed to download malware definitions'),
    }), [t]);
}

export function usePartnerTypeNames(): Record<PartnerType, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [PartnerType.Support]: t('enums:PartnerType.Support', 'Product Support'),
        [PartnerType.Sales]: t('enums:PartnerType.Sales', 'Product Sales'),
    }), [t]);
}

export function usePermissionsSourceTypeChoicesNames(): Record<PermissionsSourceTypeChoices, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [PermissionsSourceTypeChoices.Partner]: t('enums:PermissionsSourceTypeChoices.Partner', 'Partner'),
        [PermissionsSourceTypeChoices.Support]: t('enums:PermissionsSourceTypeChoices.Support', 'Support'),
    }), [t]);
}

export function useProductTypeNames(): Record<ProductType, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [ProductType.Standard]: t('enums:ProductType.Standard', 'Standard'),
        [ProductType.Poc]: t('enums:ProductType.Poc', 'Trial'),
        [ProductType.Demo]: t('enums:ProductType.Demo', 'Demo'),
        [ProductType.Editionpc]: t('enums:ProductType.Editionpc', 'Edition PC'),
        [ProductType.Nfr]: t('enums:ProductType.Nfr', 'Not for Resale'),
    }), [t]);
}

export function useRegionNames(): Record<Region, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [Region.Eu]: t('enums:Region.Eu', 'EU'),
        [Region.Na]: t('enums:Region.Na', 'North America'),
        [Region.Jp]: t('enums:Region.Jp', 'Japan'),
    }), [t]);
}

export function useTenantPermissionNames(): Record<TenantPermission, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [TenantPermission.ViewTenant]: t('enums:TenantPermission.ViewTenant', 'View Tenant'),
        [TenantPermission.ManageTenant]: t('enums:TenantPermission.ManageTenant', 'Manage Tenant'),
        [TenantPermission.AddPartner]: t('enums:TenantPermission.AddPartner', 'Add Partner'),
        [TenantPermission.RemovePartner]: t('enums:TenantPermission.RemovePartner', 'Remove Partner'),
        [TenantPermission.ViewUsers]: t('enums:TenantPermission.ViewUsers', 'View Users'),
        [TenantPermission.CreateUsers]: t('enums:TenantPermission.CreateUsers', 'Create Users'),
        [TenantPermission.UpdateUsers]: t('enums:TenantPermission.UpdateUsers', 'Update Users'),
        [TenantPermission.DeleteUsers]: t('enums:TenantPermission.DeleteUsers', 'Delete Users'),
        [TenantPermission.ViewLicenses]: t('enums:TenantPermission.ViewLicenses', 'View Licenses'),
        [TenantPermission.ActivateLicense]: t('enums:TenantPermission.ActivateLicense', 'Activate License'),
        [TenantPermission.ActivateTrial]: t('enums:TenantPermission.ActivateTrial', 'Activate Trial License'),
        [TenantPermission.TransferLicense]: t('enums:TenantPermission.TransferLicense', 'Transfer License'),
        [TenantPermission.UseEnterpriseSkus]: t('enums:TenantPermission.UseEnterpriseSkus', 'Use Enterprise SKUs'),
        [TenantPermission.CreateProvisionalLicense]: t('enums:TenantPermission.CreateProvisionalLicense', 'Create Provisional License'),
        [TenantPermission.ViewDevices]: t('enums:TenantPermission.ViewDevices', 'View Devices'),
        [TenantPermission.ManageDevices]: t('enums:TenantPermission.ManageDevices', 'Manage Devices'),
        [TenantPermission.DissociateDevices]: t('enums:TenantPermission.DissociateDevices', 'Dissociate Devices'),
        [TenantPermission.ViewPolicy]: t('enums:TenantPermission.ViewPolicy', 'View Policy'),
        [TenantPermission.CreatePolicy]: t('enums:TenantPermission.CreatePolicy', 'Create Policy'),
        [TenantPermission.ViewIdmUi]: t('enums:TenantPermission.ViewIdmUi', 'Access Wolf Portal UI'),
    }), [t]);
}

export function useTenantTypeNames(): Record<TenantType, string> {
    const {t} = useTranslation(['enums']);
    return useMemo(() => ({
        [TenantType.Customer]: t('enums:TenantType.Customer', 'Customer'),
        [TenantType.Prospect]: t('enums:TenantType.Prospect', 'Prospect'),
        [TenantType.Partner]: t('enums:TenantType.Partner', 'Partner'),
    }), [t]);
}
